import {
  type ExecutionEngineClient,
  type ExecutionEngineOperation,
  type ExecutionLiveEvent,
  type ExecutionLiveSnapshot,
} from "@repo/execution-engine-client";
import { useEffect, useMemo, useRef, useState } from "react";
import { getBrowserExecutionEngineClient } from "~/lib/execution-engine/browser-client";
import {
  type ResilientReconnectSignals,
  useResilientLiveSubscription,
} from "./resilient-live-subscription";

type ExecutionLiveOperation = ExecutionLiveSnapshot["operation"];
type TimelineEvent = ExecutionLiveSnapshot["events"][number];

export type ExecutionOperationLiveStatus =
  | "idle"
  | "connecting"
  | "live"
  // The live stream gave up reconnecting, but the operation itself is still
  // in flight server-side. This is NOT a failure — consumers must keep
  // showing the in-progress operation while a slow REST poll recovers it.
  | "disconnected"
  | "completed"
  | "failed";

export type ExecutionOperationLiveState = {
  readonly status: ExecutionOperationLiveStatus;
  readonly operation: ExecutionLiveOperation | null;
  readonly operationResponse: ExecutionEngineOperation | null;
  readonly snapshot: ExecutionLiveSnapshot | null;
  readonly events: readonly TimelineEvent[];
  readonly lastSequence: number;
  readonly error: string | null;
};

export type UseExecutionOperationLiveOptions = {
  readonly client?: ExecutionEngineClient | null;
  readonly WebSocketImpl?: Parameters<
    ExecutionEngineClient["subscribeLiveOperation"]
  >[1]["WebSocketImpl"];
  /** Injectable re-arm signals (tests); defaults to browser online/visibility. */
  readonly signals?: ResilientReconnectSignals;
};

const initialState: ExecutionOperationLiveState = {
  status: "idle",
  operation: null,
  operationResponse: null,
  snapshot: null,
  events: [],
  lastSequence: 0,
  error: null,
};

const isTerminalState = (
  state: ExecutionEngineOperation["state"] | ExecutionLiveOperation["state"],
): boolean =>
  state === "completed" || state === "failed" || state === "cancelled";

const statusForState = (
  state: ExecutionEngineOperation["state"] | ExecutionLiveOperation["state"],
): ExecutionOperationLiveStatus => {
  if (state === "completed") {
    return "completed";
  }

  if (state === "failed" || state === "cancelled") {
    return "failed";
  }

  return "live";
};

const titleForOperationResponseKind = (
  kind: ExecutionEngineOperation["kind"],
): string => {
  switch (kind) {
    case "deposit":
      return "Deposit";
    case "transfer":
      return "Transfer";
    case "withdraw":
      return "Withdrawal";
    case "bridge":
      return "Transfer";
    case "execution":
      return "Order";
  }
};

const liveOperationFromResponse = (
  operationResponse: ExecutionEngineOperation,
  previousOperation: ExecutionLiveOperation | null,
): ExecutionLiveOperation => {
  const updatedAt =
    operationResponse.updatedAt ??
    previousOperation?.updatedAt ??
    new Date().toISOString();
  const createdAt =
    operationResponse.createdAt ?? previousOperation?.createdAt ?? updatedAt;

  return {
    operationId: operationResponse.operationId,
    runId: operationResponse.workflowId,
    portfolioId:
      operationResponse.portfolioId ?? previousOperation?.portfolioId ?? "",
    kind: operationResponse.kind,
    state: operationResponse.state,
    title:
      previousOperation?.title ??
      titleForOperationResponseKind(operationResponse.kind),
    currentHeadline:
      operationResponse.currentHeadline ??
      previousOperation?.currentHeadline ??
      null,
    currentDetail:
      operationResponse.currentDetail ??
      previousOperation?.currentDetail ??
      null,
    currentPhase:
      operationResponse.currentPhase ?? previousOperation?.currentPhase ?? null,
    activityPhase:
      operationResponse.state === "completed"
        ? "completed"
        : operationResponse.state === "failed"
          ? "failed"
          : operationResponse.state === "cancelled"
            ? "cancelled"
            : (operationResponse.activityPhase ??
              (operationResponse.currentPhase ===
              previousOperation?.currentPhase
                ? previousOperation.activityPhase
                : undefined)),
    failureCode: operationResponse.failureCode,
    currentStep:
      operationResponse.currentStep ?? previousOperation?.currentStep ?? null,
    totalSteps:
      operationResponse.totalSteps ?? previousOperation?.totalSteps ?? null,
    refs: {
      ...previousOperation?.refs,
      workflowId: operationResponse.workflowId,
      runId: operationResponse.workflowId,
      ...(operationResponse.requestKey
        ? { requestKey: operationResponse.requestKey }
        : {}),
    },
    viewKind: previousOperation?.viewKind ?? "timeline",
    graph: previousOperation?.graph ?? null,
    startedAt:
      previousOperation?.startedAt ?? operationResponse.createdAt ?? createdAt,
    finishedAt:
      operationResponse.finishedAt ?? previousOperation?.finishedAt ?? null,
    createdAt,
    updatedAt,
  };
};

const stateWithOperationResponse = (
  current: ExecutionOperationLiveState,
  operationResponse: ExecutionEngineOperation,
  status: ExecutionOperationLiveStatus,
): ExecutionOperationLiveState => ({
  ...current,
  operation: liveOperationFromResponse(operationResponse, current.operation),
  operationResponse,
  status,
  error: null,
});

const appendLiveEvent = (
  current: readonly TimelineEvent[],
  event: ExecutionLiveEvent,
): readonly TimelineEvent[] => {
  if (
    current.some(
      (candidate) =>
        candidate.sequence === event.event.sequence ||
        candidate.dedupeKey === event.event.dedupeKey,
    )
  ) {
    return current;
  }

  return [...current, event.event].sort(
    (left, right) => left.sequence - right.sequence,
  );
};

export function useExecutionOperationLive(
  operationId: string | null | undefined,
  options: UseExecutionOperationLiveOptions = {},
): ExecutionOperationLiveState & {
  readonly isConfigured: boolean;
} {
  const configuredClient = options.client;
  const WebSocketImpl = options.WebSocketImpl;
  const signals = options.signals;
  const lastSequenceRef = useRef(0);
  const [state, setState] = useState<ExecutionOperationLiveState>(initialState);

  const client = useMemo(
    () =>
      configuredClient === undefined
        ? getBrowserExecutionEngineClient()
        : configuredClient,
    [configuredClient],
  );

  const scopeKey = client && operationId ? operationId : null;

  useEffect(() => {
    lastSequenceRef.current = state.lastSequence;
  }, [state.lastSequence]);

  // Reset per-operation state on scope change (or when torn down). Reconnects
  // keep the same scope, so the operation/timeline survive a dropped socket.
  useEffect(() => {
    lastSequenceRef.current = 0;
    setState(
      scopeKey ? { ...initialState, status: "connecting" } : initialState,
    );
  }, [scopeKey]);

  useResilientLiveSubscription({
    enabled: scopeKey !== null,
    scopeKey,
    ...(signals ? { signals } : {}),
    getLastSequence: () => lastSequenceRef.current,
    onStatus: (connectionStatus, error) => {
      setState((current) => {
        if (current.status === "completed" || current.status === "failed") {
          // A terminal operation is not un-done by a connection blip.
          return current;
        }
        if (connectionStatus === "disconnected") {
          // A lost live connection is not a failed operation; drop to a
          // recoverable "disconnected" state and let the slow poll resolve it.
          return { ...current, status: "disconnected", error: null };
        }
        if (connectionStatus === "connecting") {
          return { ...current, status: "connecting", error };
        }
        return current;
      });
    },
    open: async (context) => {
      if (!client || !operationId) {
        return;
      }

      const operationResponse = await client.getOperation(operationId);
      if (!context.isCurrent()) {
        return;
      }
      setState((current) =>
        stateWithOperationResponse(
          current,
          operationResponse,
          statusForState(operationResponse.state),
        ),
      );
      if (isTerminalState(operationResponse.state)) {
        context.settle();
        return;
      }

      // Always fetch a fresh descriptor: a live token is short-lived, so a
      // reconnect must never reuse the previous connect's token.
      const descriptor = await client.getOperationLive(operationId);
      if (!context.isCurrent()) {
        return;
      }

      const subscription = client.subscribeLiveOperation(operationId, {
        lastSequence: context.lastSequence,
        token: descriptor.token ?? null,
        ...(WebSocketImpl ? { WebSocketImpl } : {}),
        onSnapshot: (snapshot) => {
          context.noteActivity();
          setState((current) => ({
            ...current,
            events: snapshot.events,
            lastSequence: snapshot.sequence,
            operation: snapshot.operation,
            snapshot,
            status: statusForState(snapshot.operation.state),
            error: null,
          }));
          if (isTerminalState(snapshot.operation.state)) {
            context.settle();
          }
        },
        onEvent: (event) => {
          context.noteActivity();
          setState((current) => ({
            ...current,
            events: appendLiveEvent(current.events, event),
            lastSequence: Math.max(current.lastSequence, event.sequence),
            operation:
              event.sequence < current.lastSequence
                ? current.operation
                : event.operation,
            status:
              event.sequence < current.lastSequence
                ? current.status
                : statusForState(event.operation.state),
            error: null,
          }));
          if (isTerminalState(event.operation.state)) {
            context.settle();
          }
        },
        onReplayOverflow: () => {},
        onError: (error) => {
          context.handleError(error);
        },
        onClose: () => {
          context.handleClose();
        },
      });

      context.bindSubscription({ close: () => subscription.close() });
    },
    recover: async (context) => {
      if (!client || !operationId) {
        return "terminal";
      }

      const operationResponse = await client.getOperation(operationId);
      if (!context.isCurrent()) {
        return "pending";
      }
      if (isTerminalState(operationResponse.state)) {
        setState((current) =>
          stateWithOperationResponse(
            current,
            operationResponse,
            statusForState(operationResponse.state),
          ),
        );
        return "terminal";
      }
      setState((current) =>
        stateWithOperationResponse(current, operationResponse, current.status),
      );
      return "recovered";
    },
  });

  return {
    ...state,
    isConfigured: client !== null,
  };
}
